import { Box, Divider, Flex, Text, VisuallyHidden } from '@chakra-ui/react';
import { loadFonts } from 'hooks/useFont';
import { memo, useCallback, useEffect, useState } from 'react';
import { CustomFontState } from 'types/customFont.types';

import { useDocumentFonts } from './DocumentFonts';
import { FONT_FAMILIES } from './FontFamilySelect.config';
import { FontFamilySelectMenuProps, FontOption, FontOptionListProps } from './FontFamilySelectMenu.types';
import { FontFamilySelectMenuBrand } from './FontFamilySelectMenuBrand';
import { FontListOption } from './FontListOption';
import { SelectMenu, SelectMenuList, SelectMenuSearch } from './SelectList';
import { DOCUMENT_FONT_LIST_ID } from './SelectList.config';
import { Option } from './SelectList.types';

const FontFamilySelectMenu = ({
  onClose,
  onChange,
  fontFamily,
  heading,
  label,
  documentFontHeading,
  noMatchingFonts,
  shouldLoadFont = true,
  isMultipleFont = false,
}: FontFamilySelectMenuProps) => {
  const [fontFamilyOptions, setFontFamilyOptions] = useState<FontOption[]>(FONT_FAMILIES);
  const [fontIdLoading, setFontIdLoading] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const { customFontsMap, showBrandSection, addDocumentFont } = useDocumentFonts();

  const labelId = 'search-label';
  const hasSearchResults = fontFamilyOptions.length > 0;

  const onSelectFont = useCallback(
    async (fontFamily: string) => {
      if (fontIdLoading) return;

      setFontIdLoading(fontFamily);
      try {
        if (shouldLoadFont) await loadFonts([fontFamily], customFontsMap);
      } catch (error) {
        // TODO: handle font loading error
      } finally {
        setFontIdLoading('');
      }

      addDocumentFont(fontFamily);
      onChange(fontFamily);
    },
    [customFontsMap, fontIdLoading, onChange, addDocumentFont, shouldLoadFont],
  );

  const onFilterFonts = (filteredList: Option[]) => setFontFamilyOptions(filteredList as FontOption[]);

  return (
    <SelectMenu value={fontFamily} options={fontFamilyOptions as Option[]} onChange={onSelectFont} onClose={onClose}>
      <SelectMenuSearch
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        unfilteredOptions={FONT_FAMILIES}
        onFilter={onFilterFonts}
      />
      <SelectMenuList label={labelId} testId='font-select-menu-list' maxH='none' flexGrow={1}>
        <VisuallyHidden>
          <Text id={labelId}>{label}</Text>
        </VisuallyHidden>
        {!isSearching && (
          <>
            {showBrandSection && <FontFamilySelectMenuBrand fontFamily={fontFamily} />}
            <Text px={3} fontSize='xs' color='font.300'>
              {documentFontHeading}
            </Text>
            <DocumentFontOptionList
              fontFamily={fontFamily}
              isMultipleFont={isMultipleFont}
              customFonts={customFontsMap ?? new Map()}
            />
            <Box px={3}>
              <Divider />
            </Box>
            <Text px={3} fontSize='xs' color='font.300'>
              {heading}
            </Text>
          </>
        )}
        {hasSearchResults ? (
          <FontOptionList
            fontFamilyOptions={fontFamilyOptions}
            fontFamily={fontFamily}
            fontIdLoading={fontIdLoading}
            isMultipleFont={isMultipleFont}
          />
        ) : (
          <Text textAlign='center' px={4} py={2} fontSize='xs' color='font.300'>
            {noMatchingFonts}
          </Text>
        )}
      </SelectMenuList>
    </SelectMenu>
  );
};

const FontOptionList = memo(({ fontFamilyOptions, fontFamily, fontIdLoading, isMultipleFont }: FontOptionListProps) => {
  return (
    <Flex direction='column' px={4}>
      {fontFamilyOptions.map((option: FontOption) => {
        const { value, image_url } = option;
        const isSelected = !isMultipleFont && value === fontFamily;
        const isLoading = value === fontIdLoading;

        return (
          <FontListOption
            key={value}
            value={value}
            imageUrl={image_url}
            isSelected={isSelected}
            isLoading={isLoading}
          />
        );
      })}
    </Flex>
  );
});
FontOptionList.displayName = 'FontOptionList';

interface DocumentFontOptionListProps {
  fontFamily: string;
  customFonts: Map<string, CustomFontState>;
  isMultipleFont: boolean;
}

const DocumentFontOptionList = memo(({ fontFamily, customFonts, isMultipleFont }: DocumentFontOptionListProps) => {
  const { documentFonts, addDocumentFont } = useDocumentFonts();

  // Use the effect to update document fonts after the font dropdown is updated.
  // This fix the issue that updating the font provider while updating the dropdown who is depending on provider.
  useEffect(() => {
    // if the fontFamily value is not in the documentFonts array, add it
    if (documentFonts && !documentFonts.includes(fontFamily) && !isMultipleFont) {
      addDocumentFont(fontFamily);
    }
  }, [documentFonts, fontFamily, addDocumentFont, isMultipleFont]);

  return (
    <Flex direction='column' id={DOCUMENT_FONT_LIST_ID} px={4}>
      {documentFonts.map((value: string) => {
        const imageUrl =
          FONT_FAMILIES.find((font) => font.value === value)?.image_url ||
          (customFonts.get(value) ? `data:image/png;base64,${customFonts.get(value)!.image_base64}` : '');
        const isSelected = !isMultipleFont && value === fontFamily;

        return <FontListOption key={value} value={value} imageUrl={imageUrl} isSelected={isSelected} isDocumentFont />;
      })}
    </Flex>
  );
});
DocumentFontOptionList.displayName = 'DocumentFontOptionList';

export default FontFamilySelectMenu;
